import React, { useState } from 'react';
import { createSubmission } from '../lib/api';
import { X, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { MONTH_NAMES_PT } from '../lib/utils';

const DISTRICTS = [
  "Aveiro", "Beja", "Braga", "Bragança", "Castelo Branco",
  "Coimbra", "Évora", "Faro", "Guarda", "Leiria",
  "Lisboa", "Portalegre", "Porto", "Santarém", "Setúbal",
  "Viana do Castelo", "Vila Real", "Viseu",
  "Açores", "Madeira"
];

const RACE_TYPES = [
  { value: 'road', label: 'Estrada' },
  { value: 'trail', label: 'Trail' },
  { value: 'walk', label: 'Caminhada' },
  { value: 'kids', label: 'Kids' },
];

export const SubmitRaceModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    location: '',
    district: '',
    race_type: 'road',
    distances: '',
    website: '',
    price: '',
    submitter_email: '',
    submitter_name: '',
    notes: '',
    is_international: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Parse distances
      const distances = formData.distances
        .split(',')
        .map(d => d.trim())
        .filter(d => d);

      await createSubmission({
        ...formData,
        distances
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({
          name: '',
          date: '',
          location: '',
          district: '',
          race_type: 'road',
          distances: '',
          website: '',
          price: '',
          submitter_email: '',
          submitter_name: '',
          notes: '',
          is_international: false
        });
      }, 3000);
    } catch (err) {
      setError('Erro ao submeter. Por favor tente novamente.');
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '15px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
          padding: '20px',
          borderRadius: '16px 16px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} color="white" />
          </button>
          <h2 style={{
            color: 'white',
            margin: 0,
            fontSize: '22px',
            fontWeight: 'bold'
          }}>
            Submeter Nova Prova
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.9)',
            margin: '8px 0 0 0',
            fontSize: '14px'
          }}>
            A prova será publicada após validação
          </p>
        </div>

        {/* Success State */}
        {success ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <CheckCircle size={64} color="#22c55e" style={{ marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 10px 0', color: '#0f172a' }}>Submissão Enviada!</h3>
            <p style={{ margin: 0, color: '#64748b' }}>
              Obrigado! A sua prova será analisada e publicada em breve.
            </p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#dc2626'
              }}>
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            {/* Nome da Prova */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Nome da Prova *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Ex: Meia Maratona de Lisboa"
                style={inputStyle}
              />
            </div>

            {/* Data */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Data *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                min="2026-01-01"
                max="2026-12-31"
                style={inputStyle}
              />
            </div>

            {/* Nacional ou Internacional */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Localização da Prova *</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_international: false }))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: formData.is_international ? '1px solid #e2e8f0' : '2px solid #0ea5e9',
                    background: formData.is_international ? 'white' : '#f0f9ff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '15px',
                    fontWeight: formData.is_international ? '400' : '600',
                    color: formData.is_international ? '#64748b' : '#0284c7'
                  }}
                >
                  🇵🇹 Portugal
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_international: true }))}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: !formData.is_international ? '1px solid #e2e8f0' : '2px solid #0ea5e9',
                    background: !formData.is_international ? 'white' : '#f0f9ff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '15px',
                    fontWeight: !formData.is_international ? '400' : '600',
                    color: !formData.is_international ? '#64748b' : '#0284c7'
                  }}
                >
                  🌍 Internacional
                </button>
              </div>
            </div>

            {/* Local e Distrito */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Local *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder={formData.is_international ? "Ex: Paris, França" : "Ex: Cascais"}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Distrito {!formData.is_international && '*'}</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  required={!formData.is_international}
                  disabled={formData.is_international}
                  style={{
                    ...inputStyle,
                    background: formData.is_international ? '#f1f5f9' : 'white',
                    color: formData.is_international ? '#94a3b8' : 'inherit'
                  }}
                >
                  <option value="">{formData.is_international ? 'N/A' : 'Selecione...'}</option>
                  {DISTRICTS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tipo */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Tipo de Prova *</label>
              <select
                name="race_type"
                value={formData.race_type}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                {RACE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Distâncias */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Distâncias</label>
              <input
                type="text"
                name="distances"
                value={formData.distances}
                onChange={handleChange}
                placeholder="Ex: 5km, 10km, 21km (separar por vírgula)"
                style={inputStyle}
              />
            </div>

            {/* Website e Preço */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://..."
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Preço</label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Ex: 15€"
                  style={inputStyle}
                />
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />

            {/* Dados do Submissor */}
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px 0' }}>
              Os seus dados (apenas para contacto, não serão publicados)
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Seu Nome</label>
                <input
                  type="text"
                  name="submitter_name"
                  value={formData.submitter_name}
                  onChange={handleChange}
                  placeholder="Nome (opcional)"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Seu Email *</label>
                <input
                  type="email"
                  name="submitter_email"
                  value={formData.submitter_email}
                  onChange={handleChange}
                  required
                  placeholder="email@exemplo.com"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Notas */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Notas adicionais</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Informações adicionais sobre a prova..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '10px',
                border: 'none',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Send size={18} />
              {loading ? 'A enviar...' : 'Submeter Prova'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SubmitRaceModal;
